/**
 * OKS-697 — Canvas crop seam for the mission-cover crop step.
 *
 * `react-easy-crop` reports the selected crop as a rectangle in the
 * source image's natural pixel space (`croppedAreaPixels`). This helper
 * rasterises that rectangle to a `Blob` via an off-screen `<canvas>` so
 * the result can be staged into the existing hidden
 * `<input type="file" name="mission[cover_image]">` and submitted through
 * the parent Inertia `<Form>` as multipart — no new upload wiring.
 *
 * Extracted as a small, pure, side-effect-light function (no React) so:
 *   - the field/cropper component tests can MOCK it (jsdom has no real
 *     canvas raster) and assert only the plumbing (crop area → Blob →
 *     staged file), and
 *   - the real pixel behaviour is exercised by the Playwright E2E in a
 *     browser with a genuine canvas.
 *
 * The output MIME defaults to JPEG (covers are photographic; JPEG keeps
 * the multipart payload well under the 5 MB server ceiling). A caller can
 * pass the source file's own type to preserve PNG/WEBP when desired.
 */

export interface CropAreaPixels {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Decode an image URL (object URL or data URL) into an `HTMLImageElement`.
 * `crossOrigin`/anonymous is set so a canvas drawn from a same-origin or
 * CORS-enabled source stays untainted and `toBlob` can read it back.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image()
    image.addEventListener("load", () => resolve(image))
    image.addEventListener("error", () =>
      reject(new Error("mission-cover: could not decode image for crop")),
    )
    image.crossOrigin = "anonymous"
    image.src = src
  })
}

/**
 * Rasterise `croppedAreaPixels` of `imageSrc` to a `Blob`.
 *
 * @param imageSrc          object/data URL of the picked source image
 * @param croppedAreaPixels crop rect in the source's natural pixel space
 *                          (as reported by `react-easy-crop`)
 * @param mimeType          output type; defaults to `image/jpeg`
 * @returns the cropped image as a `Blob`
 * @throws if the image can't decode or the canvas can't export a Blob
 */
export async function getCroppedBlob(
  imageSrc: string,
  croppedAreaPixels: CropAreaPixels,
  mimeType = "image/jpeg",
): Promise<Blob> {
  const image = await loadImage(imageSrc)

  const canvas = document.createElement("canvas")
  canvas.width = Math.round(croppedAreaPixels.width)
  canvas.height = Math.round(croppedAreaPixels.height)

  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("mission-cover: 2D canvas context unavailable")

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error("mission-cover: canvas export produced no Blob"))
      },
      mimeType,
      // JPEG quality — visually lossless for a card cover, keeps the
      // multipart body small (well under the 5 MB server ceiling).
      0.92,
    )
  })
}
